import React, { useEffect, useRef, useState } from 'react';
import { Buffer } from 'buffer';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UploadCloud, Loader2 } from 'lucide-react';
import { cn, getNetlifyFunctionsRoot } from '@/lib/utils';
import { usePanelBreadcrumb } from '../components/PanelBreadcrumbContext';
import EventDateTimePicker from '../components/EventDateTimePicker';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import topGradient from '@/assets/top-gradient.png';

type WizardStep = 0 | 1 | 2;

type EventCreationState = {
  name: string;
  date: Date | null;
  slug: string;
  coverFile: File | null;
  clientName: string;
  clientEmail: string;
};

const baseUrl = 'https://app.bloomkeep.site/';

const steps: Array<{ label: string }> = [
  { label: 'Información' },
  { label: 'Diseño' },
  { label: 'Cliente' },
];

const EventCreateWizard: React.FC = () => {
  const { setTrail } = usePanelBreadcrumb();
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>(0);
  const [state, setState] = useState<EventCreationState>({
    name: '',
    date: null,
    slug: '',
    coverFile: null,
    clientName: '',
    clientEmail: '',
  });
  const [draftDate, setDraftDate] = useState<Date | null>(null);
  const [coverPreview, setCoverPreview] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Validation errors
  const [infoErrors, setInfoErrors] = useState<{ name?: string; date?: string; slug?: string }>({});
  const [clientErrors, setClientErrors] = useState<{ name?: string; email?: string }>({});

  useEffect(() => {
    setTrail([
      { label: 'Panel', to: '/panel' },
      { label: 'Eventos', to: '/panel/eventos/listado' },
      { label: 'Crear' },
    ]);
    return () => setTrail(null);
  }, [setTrail]);

  useEffect(() => {
    setDraftDate(state.date);
  }, [state.date]);

  useEffect(() => {
    if (!state.slug) {
      setSlugAvailable(null);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setIsCheckingSlug(true);
      const { data, error } = await supabase
        .from('albums')
        .select('id')
        .eq('slug', state.slug)
        .maybeSingle();

      if (error) {
        console.error('Error checking slug availability:', error);
        setSlugAvailable(null);
      } else {
        setSlugAvailable(!data);
      }
      setIsCheckingSlug(false);
    }, 600);

    return () => clearTimeout(delayDebounce);
  }, [state.slug]);

  useEffect(() => {
    if (!state.coverFile) {
      setCoverPreview('');
      return;
    }
    const objectUrl = URL.createObjectURL(state.coverFile);
    setCoverPreview(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [state.coverFile]);

  const handleGoBack = () => {
    if (step === 0) {
      navigate('/panel/eventos/listado');
    } else {
      setStep(Math.max(0, step - 1) as WizardStep);
    }
  };

  const handleNext = () => {
    if (step === 0) {
      const errs: { name?: string; date?: string; slug?: string } = {};
      if (!state.name.trim()) errs.name = 'Campo requerido';
      if (!draftDate && !state.date) errs.date = 'Campo requerido';
      if (!state.slug.trim()) errs.slug = 'Campo requerido';
      setInfoErrors(errs);
      if (Object.keys(errs).length > 0) return;
    }
    // Step 1 has no required fields (cover optional)
    setStep(Math.min(2, step + 1) as WizardStep);
  };

  const handleSubmit = async () => {
    // Validate required fields across steps before submit
    const errsInfo: { name?: string; date?: string; slug?: string } = {};
    if (!state.name.trim()) errsInfo.name = 'Campo requerido';
    if (!state.slug.trim()) errsInfo.slug = 'Campo requerido';
    if (!state.date && !draftDate) errsInfo.date = 'Campo requerido';
    setInfoErrors(errsInfo);

    const errsClient: { name?: string; email?: string } = {};
    if (!state.clientName.trim()) errsClient.name = 'Campo requerido';
    const email = state.clientEmail.trim();
    if (!email) errsClient.email = 'Campo requerido';
    else if (!/^\S+@\S+\.\S+$/.test(email)) errsClient.email = 'Ingresá un e-mail válido';
    setClientErrors(errsClient);

    if (Object.keys(errsInfo).length > 0 || Object.keys(errsClient).length > 0) return;

    if (slugAvailable === false) {
      toast.error('El slug elegido ya está en uso. Por favor elegí otro.');
      return;
    }

    setIsCreating(true);
    try {
      let coverPayload = null;
      if (state.coverFile) {
        const buffer = await state.coverFile.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const base64 = Buffer.from(bytes).toString('base64');
        const extension = state.coverFile.name.split('.').pop() || 'jpg';
        const fileName = `cover-${Date.now()}.${extension}`;
        coverPayload = { base64, fileName };
      }

      const response = await fetch(`${getNetlifyFunctionsRoot()}/create-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: state.name,
          slug: state.slug,
          eventDate: (state.date || draftDate) ? (state.date || draftDate)!.toISOString() : null,
          client: state.clientEmail
            ? { name: state.clientName, email: state.clientEmail }
            : null,
          coverFile: coverPayload,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || 'No se pudo crear el evento');
      }

      toast.success('Evento creado exitosamente');
      if (payload?.id) {
        navigate(`/panel/eventos/${payload.id}`);
      } else {
        navigate('/panel/eventos/listado');
      }
    } catch (error) {
      console.error('Error creando el evento:', error);
      toast.error('No se pudo crear el evento. Intentá nuevamente.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
    if (!allowedTypes.includes(file.type)) {
      window.alert('Formato no soportado. Subí una imagen JPG, PNG, WEBP o AVIF.');
      event.target.value = '';
      return;
    }

    // Max size: 1 MB
    const MAX_BYTES = 1 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      // Notify and discard the image selection
      try { toast.error('El archivo supera el tamaño máximo de 1 MB.'); } catch { }
      event.target.value = '';
      return;
    }

    setState((prev) => ({ ...prev, coverFile: file }));
  };

  const renderStepper = () => {
    const DOT_DIAMETER = 16;
    const dotBaseClass = 'flex items-center justify-center rounded-full border-2 transition-colors duration-300 bg-white';

    return (
      <div className="mb-10 px-2 sm:px-6 relative">
        <img src={topGradient} alt="" className="pointer-events-none select-none absolute -top-6 left-1/2 -translate-x-1/2 w-full max-w-4xl" />

        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-5 py-3">Crear Nuevo Evento</h1>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center w-full">
            {steps.map((stepItem, index) => {
              const isActive = index === step;
              const isCompleted = index < step;
              const dotClasses = cn(
                dotBaseClass,
                isActive || isCompleted ? 'border-purple-500 bg-purple-500' : 'border-purple-200'
              );

              return (
                <React.Fragment key={stepItem.label}>
                  <div
                    className={dotClasses}
                    style={{ width: DOT_DIAMETER, height: DOT_DIAMETER }}
                    aria-hidden
                  />
                  {index < steps.length - 1 && (
                    <div className="flex-1 h-0.5 mx-2 rounded-full">
                      <div
                        className={cn(
                          'h-full w-full rounded-full transition-colors duration-300',
                          index < step ? 'bg-gradient-to-r from-purple-500 to-purple-400' : 'bg-purple-200'
                        )}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] sm:text-xs font-semibold tracking-wide uppercase">
            {steps.map((stepItem, index) => (
              <span key={stepItem.label} className={cn(index <= step ? 'text-slate-900 w-8' : 'text-slate-400 w-8')}>
                {stepItem.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderInformationStep = () => (
    <div className="flex flex-col items-center gap-6">
      <div className="w-full max-w-md space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Nombre del evento</label>
          <Input
            value={state.name}
            placeholder="Ej. Boda Sofi & Juan"
            onChange={(event) => { setState((prev) => ({ ...prev, name: event.target.value })); if (infoErrors.name) setInfoErrors(prev => ({ ...prev, name: undefined })); }}
            className="h-12 rounded-2xl border-slate-200 bg-white/95 text-sm"
          />
          {infoErrors.name && (<p className="text-xs text-red-500 mt-1">{infoErrors.name}</p>)}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Fecha del evento</label>
          <EventDateTimePicker
            value={state.date}
            draftValue={draftDate}
            onDraftChange={(d) => { setDraftDate(d); /* keep error until user clicks Guardar */ }}
            onSave={() => { setState((prev) => ({ ...prev, date: draftDate })); if (infoErrors.date) setInfoErrors(prev => ({ ...prev, date: undefined })); }}
          />
          {infoErrors.date && (<p className="text-xs text-red-500 mt-1">{infoErrors.date}</p>)}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">URL del evento</label>
          <div className="flex h-12 items-center rounded-2xl border border-slate-200 bg-white/95 px-4 text-sm text-slate-600 relative">
            <span className="hidden sm:inline">{baseUrl}</span>
            <Input
              value={state.slug}
              placeholder="slug-del-evento"
              onChange={(event) => { setState((prev) => ({ ...prev, slug: event.target.value.trim().toLowerCase() })); if (infoErrors.slug) setInfoErrors(prev => ({ ...prev, slug: undefined })); }}
              className="ml-0 w-full border-none bg-transparent p-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <div className="absolute right-4">
              {isCheckingSlug ? (
                <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
              ) : slugAvailable === true ? (
                <span className="text-green-500">✅</span>
              ) : slugAvailable === false ? (
                <span className="text-red-500">❌</span>
              ) : null}
            </div>
          </div>
          {slugAvailable === false && (
            <p className="text-xs text-red-500 mt-1">Esta URL ya está en uso. Elegí otra.</p>
          )}
          {infoErrors.slug && (<p className="text-xs text-red-500 mt-1">{infoErrors.slug}</p>)}
        </div>
      </div>
    </div>
  );

  const renderDesignStep = () => (
    <div className="flex flex-col items-center gap-6">
      <div className="w-full max-w-xl">
        <div
          role="button"
          tabIndex={0}
          onClick={handleFileButtonClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleFileButtonClick();
            }
          }}
          className={cn(
            'mx-auto flex h-64 w-64 sm:h-80 sm:w-80 cursor-pointer flex-col items-center justify-center rounded-[32px] border-2 border-dashed border-purple-300 bg-white/70 text-center text-sm text-slate-500 transition',
            coverPreview && 'border-purple-400 bg-white shadow-inner'
          )}
        >
          {coverPreview ? (
            <img src={coverPreview} alt="Portada seleccionada" className="h-full w-full rounded-[28px] object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-3">
              <UploadCloud className="h-10 w-10 text-purple-400" />
              <p>Elegir imagen de portada</p>
            </div>
          )}
        </div>
        <div className="mt-6 flex items-center justify-center">
          {coverPreview && (
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-purple-200 bg-white text-xs font-medium text-purple-500"
              onClick={handleFileButtonClick}
            >
              Cambiar imagen
            </Button>
          )}
        </div>
        <p className="mt-2 text-center text-xs text-slate-400">Se puede elegir o cambiar luego</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.avif"
          className="hidden"
          onChange={handleFileChange}
        />
        {state.coverFile && (
          <p className="mt-3 text-center text-xs text-slate-400">{state.coverFile.name}</p>
        )}
      </div>
    </div>
  );

  const renderClientStep = () => (
    <div className="flex flex-col items-center gap-8">
      <div className="w-full max-w-md space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Nombre del cliente</label>
          <Input
            value={state.clientName}
            placeholder="Nombre y apellido"
            onChange={(event) => { setState((prev) => ({ ...prev, clientName: event.target.value })); if (clientErrors.name) setClientErrors(prev => ({ ...prev, name: undefined })); }}
            className="h-12 rounded-2xl border-slate-200 bg-white/95 text-sm"
          />
          {clientErrors.name && (<p className="text-xs text-red-500 mt-1">{clientErrors.name}</p>)}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">E-mail del cliente</label>
          <Input
            value={state.clientEmail}
            placeholder="cliente@correo.com"
            onChange={(event) => { setState((prev) => ({ ...prev, clientEmail: event.target.value })); if (clientErrors.email) setClientErrors(prev => ({ ...prev, email: undefined })); }}
            className="h-12 rounded-2xl border-slate-200 bg-white/95 text-sm"
            type="email"
          />
          {clientErrors.email && (<p className="text-xs text-red-500 mt-1">{clientErrors.email}</p>)}
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    if (step === 0) return renderInformationStep();
    if (step === 1) return renderDesignStep();
    return renderClientStep();
  };

  return (
    <div className="space-y-10">
      {renderStepper()}
      <div className="panel-card panel-card--main p-6 sm:p-10 bg-white/70">
        {isCreating ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-purple-500" />
            <p className="text-lg font-medium text-slate-700">Creando tu evento</p>
          </div>
        ) : (
          <>
            {renderStepContent()}
            <div className="mt-10 flex items-center justify-between">
              <button
                type="button"
                onClick={handleGoBack}
                className="text-sm font-medium text-purple-500 hover:text-purple-600"
              >
                &lt; Volver
              </button>
              {step < 2 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  className="rounded-full bg-gradient-to-r from-purple-500 to-purple-400 px-6 py-2 text-sm font-semibold hover:text-white hover:from-pink-500 hover:to-pink-400  text-white shadow-[0_12px_24px_rgba(124,77,255,0.25)] hover:shadow-[0_14px_28px_rgba(124,77,255,0.3)]"
                >
                  Siguiente
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isCreating || !state.clientName.trim() || !state.clientEmail.trim()}
                  className="rounded-full bg-gradient-to-r from-pink-400 to-purple-500 px-8 py-2 text-sm font-semibold hover:text-white disabled:text-white text-white shadow-[0_14px_30px_rgba(214,31,105,0.35)] hover:shadow-[0_16px_34px_rgba(214,31,105,0.4)]"
                >
                  Crear evento
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EventCreateWizard;
